'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Key, Plus } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useApiKeysList } from '@/lib/api/apiKeys';
import { AccessTokensTable } from './components/access-tokens-table';
import { AccessTokensSkeleton } from './components/access-tokens-skeleton';
import { TokenFormDialog } from './components/token-form-dialog';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(AccessTokensPage, PERMISSIONS.TEAM_MANAGE);

function AccessTokensPage() {
  const { t } = useTranslation('settings');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const pageSize = 20;

  const { data, isLoading, error } = useApiKeysList({
    pagination: { page, pageSize },
    sortBy: { field: 'createdAt', direction: 'desc' },
  });

  if (isLoading) return <AccessTokensSkeleton />;

  if (error) {
    return (
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">{t('accessTokens.errorLoading')} {error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold tracking-tight">{t('accessTokens.title')}</h2>
              <Badge variant="outline" className="text-xs">
                {data?.apiKeys?.filter((token: any) => !token.revokedAt).length || 0} {t('accessTokens.active')}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              {t('accessTokens.description')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('accessTokens.createAccessToken')}
          </Button>
        </div>
      </div>

      {/* Access tokens table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('accessTokens.apiAccessTokens')}</CardTitle>
          <CardDescription>{t('accessTokens.viewAndManage')}</CardDescription>
        </CardHeader>
        <CardContent>
          <AccessTokensTable
            data={data?.apiKeys || []}
            pageCount={data?.pageCount || 0}
            page={page}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      <TokenFormDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} mode="create" />
    </div>
  );
}
